import {
  normalizeKey,
  resolveCanonicalListValue,
} from '../../shared/verteoffice/exports/helpers/helpers'
import type { CellEditHandler, SheetDefinition } from '../../shared/verteoffice'
import { buildDependencyConfiguration } from './dependencyConfig'
import type { DependencyConfig, DomainDataList } from './dependencyConfig'
import type { DomainNode } from './buildDomainNodes'

export interface ActivityCascadePath {
  general_activity: string
  specific_activity: string
  activity_objective: string
}

const TRIGGER_COLUMNS = new Set([
  'general_activity',
  'specific_activity',
  'activity_objective',
])

const isSame = (left: unknown, right: unknown): boolean =>
  normalizeKey(String(left ?? '')) === normalizeKey(String(right ?? ''))

const canonicalFromList = (value: unknown, options: string[] | undefined): string =>
  resolveCanonicalListValue(value, options ?? [], 'normalized') ?? String(value ?? '').trim()

const uniqueByPath = (paths: ActivityCascadePath[]): ActivityCascadePath[] => {
  const seen = new Set<string>()
  return paths.filter((path) => {
    const key = [
      normalizeKey(path.general_activity),
      normalizeKey(path.specific_activity),
      normalizeKey(path.activity_objective),
    ].join('|')
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

// Se cachea contra el DependencyConfig ya resuelto, no contra domainDataList:
// el config depende tambien de los nodos de BD, y buildDependencyConfiguration
// devuelve el mismo objeto para la misma pareja de entradas.
const pathCache = new WeakMap<DependencyConfig, ActivityCascadePath[]>()

export function buildActivityCascadePaths(
  domainDataList: DomainDataList,
  nodes?: DomainNode[]
): ActivityCascadePath[] {
  const dependencyConfig = buildDependencyConfiguration(domainDataList, nodes)
  const cached = pathCache.get(dependencyConfig)
  if (cached) return cached

  const generalOptions = dependencyConfig.general_activity?.options ?? {}
  const objectiveOptions = dependencyConfig.specific_activity?.options ?? {}
  const paths: ActivityCascadePath[] = []

  Object.entries(generalOptions).forEach(([general, options]) => {
    if (general === 'default') return
    const canonicalGeneral = canonicalFromList(general, domainDataList.generalActivityList)
    const objectives = options.specific_activity ?? []
    objectives.forEach((objective) => {
      const objectiveKey = Object.keys(objectiveOptions)
        .find(key => key !== 'default' && isSame(key, objective))
      const specifics = objectiveKey
        ? objectiveOptions[objectiveKey]?.activity_objective ?? []
        : []

      specifics.forEach((specific) => {
        paths.push({
          general_activity: canonicalGeneral,
          specific_activity: objective,
          activity_objective: specific,
        })
      })
    })
  })

  const uniquePaths = uniqueByPath(paths)
  pathCache.set(dependencyConfig, uniquePaths)
  return uniquePaths
}

export function resolveGeneralForObjective(
  objective: unknown,
  domainDataList: DomainDataList,
  nodes?: DomainNode[]
): string | null {
  const dependencyConfig = buildDependencyConfiguration(domainDataList, nodes)
  const matches = Object.entries(dependencyConfig.general_activity?.options ?? {})
    .filter(([general]) => general !== 'default')
    .filter(([, options]) => (options.specific_activity ?? []).some(item => isSame(item, objective)))
    .map(([general]) => canonicalFromList(general, domainDataList.generalActivityList))

  const uniqueMatches = Array.from(new Set(matches))
  return uniqueMatches.length === 1 ? uniqueMatches[0] : null
}

export function resolvePathForSpecific(
  record: Record<string, unknown>,
  domainDataList: DomainDataList,
  nodes?: DomainNode[]
): ActivityCascadePath | null {
  const specific = String(record.activity_objective ?? '').trim()
  if (!specific) return null

  let paths = buildActivityCascadePaths(domainDataList, nodes)
    .filter(path => isSame(path.activity_objective, specific))

  const general = String(record.general_activity ?? '').trim()
  if (general) {
    paths = paths.filter(path => isSame(path.general_activity, general))
  }

  const objective = String(record.specific_activity ?? '').trim()
  if (objective) {
    paths = paths.filter(path => isSame(path.specific_activity, objective))
  }

  const uniquePaths = uniqueByPath(paths)
  return uniquePaths.length === 1 ? uniquePaths[0] : null
}

export function applyActivityCascadeToRecord<T extends Record<string, any>>(
  record: T,
  domainDataList: DomainDataList,
  nodes?: DomainNode[]
): T {
  const nextRecord = { ...record }
  const specificPath = resolvePathForSpecific(nextRecord, domainDataList, nodes)

  if (specificPath) {
    if (!String(nextRecord.general_activity ?? '').trim()) {
      nextRecord.general_activity = specificPath.general_activity
    }
    if (!String(nextRecord.specific_activity ?? '').trim()) {
      nextRecord.specific_activity = specificPath.specific_activity
    }
    return nextRecord
  }

  if (!String(nextRecord.general_activity ?? '').trim()) {
    const general = resolveGeneralForObjective(nextRecord.specific_activity, domainDataList, nodes)
    if (general) {
      nextRecord.general_activity = general
    }
  }

  return nextRecord
}

function columnIndex(sheetDefinition: SheetDefinition, key: string): number {
  return sheetDefinition.columns.findIndex(column => column.key === key)
}

function readCell(worksheet: any, univerAPI: any, row: number, column: number): unknown {
  if (column < 0) return undefined
  const addr = `${univerAPI.Util.tools.chatAtABC(column)}${row + 1}`
  return worksheet.getRange(addr).getValue()
}

function writeCellIfChanged(
  worksheet: any,
  univerAPI: any,
  row: number,
  column: number,
  value: unknown
) {
  if (column < 0) return
  const addr = `${univerAPI.Util.tools.chatAtABC(column)}${row + 1}`
  const range = worksheet.getRange(addr)
  const currentValue = range.getValue()
  if (normalizeKey(String(currentValue ?? '')) === normalizeKey(String(value ?? ''))) return
  range.setValue(value)
}

function readActivityRecord(
  worksheet: any,
  univerAPI: any,
  row: number,
  sheetDefinition: SheetDefinition
): Record<string, unknown> {
  return {
    general_activity: readCell(worksheet, univerAPI, row, columnIndex(sheetDefinition, 'general_activity')),
    specific_activity: readCell(worksheet, univerAPI, row, columnIndex(sheetDefinition, 'specific_activity')),
    activity_objective: readCell(worksheet, univerAPI, row, columnIndex(sheetDefinition, 'activity_objective')),
  }
}

export function createActivityCascadeCellEditHandler(
  domainDataList: DomainDataList,
  nodes?: DomainNode[]
): CellEditHandler {
  return {
    afterEdit: ({ worksheet, univerAPI, row, columnConfig, sheetDefinition }) => {
      if (row <= 0 || !TRIGGER_COLUMNS.has(columnConfig.key)) return

      const record = readActivityRecord(worksheet, univerAPI, row, sheetDefinition)
      const generalColumn = columnIndex(sheetDefinition, 'general_activity')
      const objectiveColumn = columnIndex(sheetDefinition, 'specific_activity')

      if (columnConfig.key === 'specific_activity') {
        const general = resolveGeneralForObjective(record.specific_activity, domainDataList, nodes)
        if (general) {
          writeCellIfChanged(worksheet, univerAPI, row, generalColumn, general)
        }
        return
      }

      if (columnConfig.key === 'activity_objective' || columnConfig.key === 'general_activity') {
        const path = resolvePathForSpecific(record, domainDataList, nodes)
        if (!path) return

        writeCellIfChanged(worksheet, univerAPI, row, generalColumn, path.general_activity)
        writeCellIfChanged(worksheet, univerAPI, row, objectiveColumn, path.specific_activity)
      }
    }
  }
}
